'use client'

import React from 'react'
import classNames from 'classnames'
import { TicketLink } from '../ticket-link'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import { Maybe, ProductTypeProps } from '../cards'
import { Product as VariantCProduct } from '@dustin-web/microsite-data/src/graphql/content/generated/graphql-types'
import LineClamp from '@dustin-web/microsite-components/src/line-clamp/line-clamp'

export type Props = {
  product?: Maybe<ProductTypeProps> | VariantCProduct
  ticket: string
  onClickProduct: () => void
  className?: string
  compact?: boolean
  ellipsis?: boolean
  hideManufacturer?: boolean
  hTag?: HeadingProps['hTag']
}

const ProductCardTitle = ({
  product,
  ticket,
  onClickProduct,
  className,
  compact = false,
  ellipsis = false,
  hideManufacturer = false,
  hTag,
}: Props) => {
  if (!product) {
    return null
  }
  return (
    <Container
      displayName={product.displayName}
      id={product.id}
      nameSlug={product.nameSlug}
      displaySpecifications={product.displaySpecifications}
      manufacturerName={product.manufacturerName}
      ticket={ticket}
      onClickProduct={onClickProduct}
      className={className}
      ellipsis={ellipsis}
      compact={compact}
      hideManufacturer={hideManufacturer}
      hTag={hTag}
      showSpecificationsInProductCardTitle={!!product?.showSpecificationsInProductCardTitle}
    />
  )
}
export const EllipsisOrNot = ({
  ellipsis,
  text,
  className,
}: {
  ellipsis: boolean
  text: string
  className?: string
}) => {
  if (ellipsis) {
    return <Ellipsis className={className} text={text} />
  } else {
    return <>{text}</>
  }
}
const Ellipsis = ({ text, className }: { text: string; className?: string }) => {
  return <LineClamp className={className} text={text} />
}

type TitleProps = {
  displayName?: string | null
  id: string
  nameSlug: string
  displaySpecifications: string
  className?: string
  compact?: boolean
  manufacturerName: string
  ticket: string
  onClickProduct: () => void
  ellipsis?: boolean
  ticketClassName?: string
  manufactureNameClassName?: string
  containerClassName?: string
  hTag?: HeadingProps['hTag']
  hideManufacturer?: boolean
  showSpecificationsInProductCardTitle?: boolean
}
export const Title = ({
  displayName,
  id,
  nameSlug,
  displaySpecifications,
  className,
  compact,
  manufacturerName,
  ticket,
  onClickProduct,
  ellipsis = false,
  ticketClassName,
  manufactureNameClassName,
  containerClassName = 'c-product-card-title',
  hTag = 'h3',
  hideManufacturer,
  showSpecificationsInProductCardTitle,
}: TitleProps) => {
  if (!id || !nameSlug) {
    return null
  }

  const productUrl = getProductUrl(id, nameSlug)
  const text = [
    displayName?.trim(),
    showSpecificationsInProductCardTitle ? displaySpecifications?.trim() : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <Heading
        className={classNames(containerClassName, className, 'u-mb-0', {
          'c-product-card-title--compact': compact,
        })}
        hTag={hTag}>
        <TicketLink
          url={productUrl}
          ticket={ticket}
          className={ticketClassName || 'c-product-card-title__display-name'}
          onClickCallBack={onClickProduct}
          useAnchorTag>
          {!hideManufacturer && (
            <span
              className={
                manufactureNameClassName || 'c-product-card-title__manufacturer-name u-mb-0'
              }
              data-test-id="product-card-manufacturer-name">
              {manufacturerName}
            </span>
          )}
          <EllipsisOrNot ellipsis={ellipsis} text={text} />
        </TicketLink>
      </Heading>
    </>
  )
}

export const OldTitle = ({
  displayName,
  id,
  nameSlug,
  displaySpecifications,
  className,
  compact,
  manufacturerName,
  ticket,
  onClickProduct,
  ellipsis = false,
  ticketClassName,
  manufactureNameClassName,
  hideManufacturer,
  containerClassName = 'c-product-card-title',
  showSpecificationsInProductCardTitle,
}: TitleProps) => {
  if (!id || !nameSlug) {
    return null
  }

  const productUrl = getProductUrl(id, nameSlug)
  const text = [
    displayName?.trim(),
    showSpecificationsInProductCardTitle ? displaySpecifications?.trim() : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      {!hideManufacturer && (
        <h2
          className={manufactureNameClassName || 'c-product-card-title__manufacturer-name u-mb-0'}
          data-test-id="product-card-manufacturer-name">
          {manufacturerName}
        </h2>
      )}
      <h2
        className={classNames(containerClassName, className, 'u-mb-0', {
          'c-product-card-title--compact': compact,
        })}>
        <TicketLink
          url={productUrl}
          ticket={ticket}
          className={ticketClassName || 'c-product-card-title__display-name'}
          onClickCallBack={onClickProduct}
          useAnchorTag>
          <EllipsisOrNot ellipsis={ellipsis} text={text} />
        </TicketLink>
      </h2>
    </>
  )
}

export default ProductCardTitle

interface HeadingProps extends React.HTMLAttributes<HTMLElement> {
  hTag: string
  children: React.ReactNode
}

const Heading: React.FC<HeadingProps> = ({ hTag, children, ...props }) => {
  const Tag = hTag
  return React.createElement(Tag, props, children)
}

const Container = (props: React.JSX.IntrinsicAttributes & TitleProps) => {
  if (props.hTag) {
    return <Title {...props} />
  } else {
    return <OldTitle {...props} />
  }
}
